import type { MenuItem as ReploMenuItem } from "@replo/design-system/components/menu/Menu";
import type { Product } from "schemas/product";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useAllowScroll } from "@editor/utils/hooks/useAllowScroll";
import { trpc } from "@editor/utils/trpc";

import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { Menu, MenuTrigger } from "@replo/design-system/components/menu/Menu";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replo/design-system/components/shadcn/core/table";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import twMerge from "@replo/design-system/utils/twMerge";
import startCase from "lodash-es/startCase";
import { Grid2x2, List, MoreHorizontal, Package, Search } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

const PAGE_SIZE = 50;

type DataGridColumn = {
  field: keyof Product | "actions";
  headerName: string;
  align?: "left" | "right";
  renderCell?: (row: Product) => React.ReactNode;
};

type ViewMode = "gallery" | "list";
type StatusFilter = "all" | "active" | "draft" | "archived";

const STATUS_OPTIONS = [
  { value: "all" as const, label: "All Statuses" },
  { value: "active" as const, label: "Active" },
  { value: "draft" as const, label: "Draft" },
  { value: "archived" as const, label: "Archived" },
];

const getStatusBadgeClasses = (status: Product["status"]): string => {
  switch (status) {
    case "active":
      return "bg-positive-subtle text-positive-default";
    case "draft":
      return "bg-warning-subtle text-warning-default";
    case "archived":
      return "bg-component-subtle text-foreground-muted";
    default:
      return "bg-component text-foreground";
  }
};

const getStatusSortOrder = (product: Product): number => {
  return exhaustiveSwitch(
    product,
    "status",
  )({
    active: 1,
    draft: 2,
    archived: 3,
  });
};

const ProductLoadingSkeleton: React.FC<{ viewMode: ViewMode }> = ({
  viewMode,
}) => (
  <div className="animate-pulse">
    {viewMode === "gallery" ? (
      <div className="grid min-w-full grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, index) => (
          <Card key={index} className="flex flex-col overflow-hidden">
            <Skeleton className="aspect-square w-full" />
            <CardContent className="flex flex-grow flex-col p-3">
              <Skeleton className="h-4 mb-1 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    ) : (
      <div className="p-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-4 py-3 border-b border-border"
          >
            <Skeleton className="h-10 w-10 rounded" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    )}
  </div>
);

export function ProductsDashboard() {
  const [
    searchQueryInputValue,
    debouncedSearchQuery,
    setSearchQueryInputValue,
  ] = useDebouncedState("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [viewMode, setViewMode] = React.useState<ViewMode>("list");
  const currentProjectId = useCurrentProjectId();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingProducts,
    isFetching,
  } = trpc.product.list.useInfiniteQuery(
    {
      projectId: currentProjectId ?? "",
      filters: {
        searchTerm: debouncedSearchQuery,
        status: statusFilter === "all" ? undefined : statusFilter,
      },
      pageSize: PAGE_SIZE,
    },
    {
      enabled: Boolean(currentProjectId),
      getNextPageParam: (lastPage) => lastPage.pageInfo.nextPage,
      initialCursor: undefined,
    },
  );

  const allProducts = React.useMemo(() => {
    return data?.pages.flatMap((page) => page.products) ?? [];
  }, [data]);

  const fetchMore = React.useCallback(() => {
    if (!isFetching) {
      void fetchNextPage();
    }
  }, [fetchNextPage, isFetching]);

  useAllowScroll();

  const onProductClick = (_clickedProductId: string) => {
    // Note (Kevin 05-22-2025): This is a placeholder for product editor in the next PR
  };

  const processedProducts = React.useMemo(() => {
    const tempProducts: Product[] = [...allProducts];

    tempProducts.sort((a, b) => {
      const orderA = getStatusSortOrder(a);
      const orderB = getStatusSortOrder(b);
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.title.localeCompare(b.title);
    });

    return tempProducts;
  }, [allProducts]);

  const columns: DataGridColumn[] = [
    {
      field: "featuredMedia",
      headerName: "Featured Media",
      renderCell: (row: Product): React.ReactNode => {
        if (row.featuredMedia?.type === "image") {
          return (
            <img
              src={row.featuredMedia.url}
              alt={row.featuredMedia.alt ?? row.title}
              className="h-10 w-10 rounded object-cover"
            />
          );
        }
        return (
          <div className="bg-component text-muted flex h-10 w-10 items-center justify-center rounded">
            <Package className="h-5 w-5" />
          </div>
        );
      },
    },
    {
      field: "media",
      headerName: "Media",
      renderCell: (row: Product): React.ReactNode => {
        if (!row.media || row.media.length === 0) {
          return <div className="text-muted text-sm">No media</div>;
        }
        return (
          <div className="flex gap-1">
            {row.media.slice(0, 3).map((item, index) => {
              if (item.type === "image") {
                return (
                  <img
                    key={index}
                    src={item.url}
                    alt={item.alt ?? `Product media ${index + 1}`}
                    className="h-10 w-10 rounded object-cover"
                  />
                );
              }
              return (
                <div
                  key={index}
                  className="bg-component text-muted flex h-10 w-10 items-center justify-center rounded"
                >
                  <Package className="h-4 w-4" />
                </div>
              );
            })}
            {row.media.length > 3 && (
              <div className="bg-surface-subtle text-muted flex h-10 w-10 items-center justify-center rounded text-xs">
                +{row.media.length - 3}
              </div>
            )}
          </div>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      renderCell: (row: Product): React.ReactNode => (
        <div className="flex flex-col">
          <span className="text-foreground text-sm font-medium">
            {row.title}
          </span>
          <span className="text-muted text-xs">{row.slug ?? "N/A"}</span>
        </div>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      renderCell: (row: Product): React.ReactNode => (
        <div className="text-muted text-sm max-w-xs truncate">
          {row.description ?? "No description"}
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      renderCell: (row: Product): React.ReactNode => (
        <div
          className={twMerge(
            "inline-block rounded-full px-2 py-0.5 text-xs font-medium",
            getStatusBadgeClasses(row.status),
          )}
        >
          {startCase(row.status)}
        </div>
      ),
    },
    {
      field: "referenceType",
      headerName: "Source",
      renderCell: (row: Product): React.ReactNode => (
        <span className="text-muted text-sm">{row.referenceType ?? "N/A"}</span>
      ),
    },
    {
      field: "vendor",
      headerName: "Vendor",
      renderCell: (row: Product): React.ReactNode => (
        <span className="text-muted text-sm">{row.vendor ?? "N/A"}</span>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      renderCell: (row: Product): React.ReactNode => (
        <span className="text-muted text-sm">{row.type ?? "N/A"}</span>
      ),
    },
    {
      field: "options",
      headerName: "Options",
      renderCell: (row: Product): React.ReactNode => (
        <div className="text-muted text-sm">
          {row.options?.length ?? 0}{" "}
          {row.options?.length === 1 ? "option" : "options"}
        </div>
      ),
    },
    {
      field: "variants",
      headerName: "Variants",
      renderCell: (row: Product): React.ReactNode => (
        <div className="text-muted text-sm">
          {row.variants?.length ?? 0}{" "}
          {row.variants?.length === 1 ? "variant" : "variants"}
        </div>
      ),
    },
    {
      field: "sellingPlanGroups",
      headerName: "Selling Plans",
      renderCell: (row: Product): React.ReactNode => (
        <div className="text-muted text-sm">
          {row.sellingPlanGroups?.length ?? 0}{" "}
          {row.sellingPlanGroups?.length === 1 ? "group" : "groups"}
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "",
      align: "right",
      renderCell: (row: Product): React.ReactNode => {
        const menuItems: ReploMenuItem[] = [
          {
            type: "leaf",
            id: "edit",
            title: "Edit",
            onSelect: () => onProductClick(row.id),
            variant: "default",
          },
        ];
        return (
          <Menu
            items={menuItems}
            trigger={
              <MenuTrigger>
                <IconButton
                  variant="tertiary"
                  size="sm"
                  icon={<MoreHorizontal size={16} />}
                />
              </MenuTrigger>
            }
            align="end"
            size="sm"
          />
        );
      },
    },
  ];

  const header = (
    <div className="px-2 pt-2">
      <h1 className="text-foreground-default text-xl font-semibold">
        Products
      </h1>
      <p className="text-muted text-sm">Manage products in your workspace</p>
    </div>
  );

  if (isLoadingProducts) {
    return (
      <div className="bg-background-subtle flex min-h-screen w-full flex-col gap-4 p-4">
        {header}
        <Card className="bg-background-default w-full flex-1 overflow-hidden rounded-lg shadow-sm">
          <CardContent className="p-0">
            <div className="text-muted p-6 text-center">
              Loading products...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!processedProducts.length && !isLoadingProducts) {
    return (
      <div className="bg-background-subtle flex min-h-screen w-full flex-col gap-4 p-4">
        {header}
        <Card className="bg-background-default w-full flex-1 overflow-hidden rounded-lg shadow-sm">
          <CardContent className="p-0">
            <EmptyState
              icon={<Package size={48} />}
              title="No products found"
              description={
                debouncedSearchQuery || statusFilter !== "all"
                  ? "Try adjusting your search or filters."
                  : "Add your first product to get started!"
              }
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background-subtle flex min-h-screen w-full flex-col gap-4 p-4">
      {header}

      <Card className="bg-background-default w-full flex-1 overflow-hidden rounded-lg shadow-sm">
        <div className="border-border flex flex-col items-center justify-between gap-3 border-b p-4 sm:flex-row">
          <div className="w-full flex-grow sm:w-auto sm:max-w-xs">
            <Input
              size="base"
              placeholder="Search by product name or handle..."
              type="text"
              value={searchQueryInputValue}
              onChange={(event) => setSearchQueryInputValue(event.target.value)}
              startEnhancer={<Search className="text-muted" size={16} />}
              layoutClassName="h-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            <Combobox
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as StatusFilter);
              }}
              trigger={
                <div className="min-w-[400px] w-full justify-between text-sm h-9">
                  <Combobox.SelectionButton
                    title={
                      STATUS_OPTIONS.find((opt) => opt.value === statusFilter)
                        ?.label ?? "All Statuses"
                    }
                  />
                </div>
              }
              inputPlaceholder="Filter status..."
              areOptionsSearchable={false}
              align="end"
            />
            <ToggleGroup
              size="base"
              options={[
                {
                  label: <Grid2x2 size={16} />,
                  value: "gallery",
                  tooltipContent: "Gallery View",
                },
                {
                  label: <List size={16} />,
                  value: "list",
                  tooltipContent: "List View",
                },
              ]}
              onChange={(value) => setViewMode(value as ViewMode)}
              selectedValue={viewMode}
            />
          </div>
        </div>
        <CardContent
          className="p-0 overflow-auto"
          id="products-dashboard-content"
        >
          {isLoadingProducts && processedProducts.length === 0 ? (
            <ProductLoadingSkeleton viewMode={viewMode} />
          ) : (
            <InfiniteScroll
              dataLength={processedProducts.length}
              next={fetchMore}
              hasMore={hasNextPage}
              loader={<ProductLoadingSkeleton viewMode={viewMode} />}
              scrollableTarget="products-dashboard-content"
              className="no-scrollbar"
              style={{ overflow: "visible" }}
            >
              {viewMode === "gallery" ? (
                <div className="grid min-w-full grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
                  {processedProducts.map((product: Product) => (
                    <Card
                      key={product.id}
                      className="group flex cursor-pointer flex-col overflow-hidden hover:shadow-lg"
                      onClick={() => onProductClick(product.id)}
                    >
                      <CardHeader className="relative aspect-square overflow-hidden p-0">
                        {product.featuredMedia?.url ? (
                          <img
                            src={product.featuredMedia.url}
                            alt={product.title}
                            className="h-full w-full rounded-t-lg object-cover transition-transform duration-200 ease-in-out group-hover:scale-105"
                          />
                        ) : (
                          <div className="bg-component text-muted flex h-full w-full items-center justify-center rounded-t-lg">
                            <Package className="h-12 w-12" />
                          </div>
                        )}
                      </CardHeader>
                      <CardContent className="flex flex-grow flex-col p-3">
                        <CardTitle className="text-foreground-default mb-0.5 truncate text-sm font-medium">
                          {product.title}
                        </CardTitle>
                        <p className="text-muted flex-grow truncate text-xs">
                          {product.slug ?? "N/A"}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <span
                            className={twMerge(
                              "inline-block rounded-full px-1.5 py-0.5 text-xs font-medium",
                              getStatusBadgeClasses(product.status),
                            )}
                          >
                            {startCase(product.status)}
                          </span>
                          <Menu
                            items={[
                              {
                                type: "leaf",
                                id: "edit",
                                title: "Edit",
                                onSelect: () => onProductClick(product.id),
                                variant: "default",
                              },
                            ]}
                            trigger={
                              <MenuTrigger>
                                <IconButton
                                  variant="tertiary"
                                  size="sm"
                                  icon={<MoreHorizontal size={16} />}
                                />
                              </MenuTrigger>
                            }
                            align="end"
                            size="sm"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow className="border-border border-b">
                      {columns.map((col) => (
                        <TableHead
                          key={col.field}
                          className={twMerge(
                            "text-muted px-4 py-3 text-left text-xs font-medium tracking-wider uppercase",
                            col.align === "right" && "text-right",
                          )}
                        >
                          {col.headerName}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-border divide-y">
                    {processedProducts.map((product: Product) => (
                      <TableRow
                        key={product.id}
                        onClick={() => onProductClick(product.id)}
                        className="hover:bg-surface-hover cursor-pointer"
                      >
                        {columns.map((col) => (
                          <TableCell
                            key={col.field}
                            className={twMerge(
                              "text-foreground px-4 py-3 text-sm whitespace-nowrap",
                              col.align === "right" && "text-right",
                            )}
                          >
                            {(() => {
                              if (col.renderCell) {
                                return col.renderCell(product);
                              }
                              const value = product[col.field as keyof Product];
                              if (typeof value === "string") {
                                return value;
                              }
                              return "N/A";
                            })()}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </InfiniteScroll>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
